// Règles pures de l'écran de suivi conformité : dates, messages d'erreur, recherche, apparence des emplacements et aperçus.

export const ADDITIONAL_SLOT_TITLE = "Fichiers supplémentaires";

const DATE_FORMAT = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
};

export function formatDocumentDate(value) {
    if (!value) return "";
    try {
        return new Intl.DateTimeFormat("fr-FR", DATE_FORMAT).format(new Date(value));
    } catch {
        return "";
    }
}

export function documentTitle(document) {
    return `${document.url ? "Lien - " : ""}${document.fileName || "Document"}`;
}

export function apiErrorMessage(error, fallback) {
    const data = error?.response?.data;
    const message = data?.message || data?.error || (typeof data === "string" ? data : "");
    return message || fallback;
}

export function normalizeSearch(value) {
    return (value || "")
        .toString()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim();
}

export function documentMatchesSearch(document, searchTerm) {
    if (!searchTerm) return true;
    return [
        document.fileName,
        document.url,
        document.uploadedBy,
    ].some(value => normalizeSearch(value).includes(searchTerm));
}

function expectedRow(expected, documents, normalizedSearch, folderMatchesSearch) {
    const slotDocuments = documents.filter(document => document.documentKey === expected.key);
    const matchingDocuments = folderMatchesSearch
        ? slotDocuments
        : slotDocuments.filter(document => documentMatchesSearch(document, normalizedSearch));
    const expectedMatchesSearch = !normalizedSearch || folderMatchesSearch || normalizeSearch(expected.label).includes(normalizedSearch);

    return {
        expected,
        documents: expectedMatchesSearch ? slotDocuments : matchingDocuments,
        visible: expectedMatchesSearch || matchingDocuments.length > 0,
    };
}

// Ce qu'un dossier affiche pour une recherche : le nom du dossier garde tout, un libellé attendu garde son emplacement
// entier, sinon seuls les documents dont le nom, l'adresse ou l'auteur correspondent restent visibles.
export function folderSearchView(folder, documents, searchTerm) {
    const normalizedSearch = normalizeSearch(searchTerm);
    const folderMatchesSearch = Boolean(normalizedSearch) && normalizeSearch(folder.label).includes(normalizedSearch);
    const expectedRows = (folder.expectedDocuments || [])
        .map(expected => expectedRow(expected, documents, normalizedSearch, folderMatchesSearch))
        .filter(row => row.visible)
        .map(({ expected, documents: slotDocuments }) => ({ expected, documents: slotDocuments }));
    const allAdditionalDocuments = documents.filter(document => !document.documentKey);
    const matchingAdditionalDocuments = folderMatchesSearch
        ? allAdditionalDocuments
        : allAdditionalDocuments.filter(document => documentMatchesSearch(document, normalizedSearch));
    const showAdditionalSlot = !normalizedSearch
        || folderMatchesSearch
        || normalizeSearch(ADDITIONAL_SLOT_TITLE).includes(normalizedSearch)
        || matchingAdditionalDocuments.length > 0;

    return {
        normalizedSearch,
        expectedRows,
        additionalDocuments: normalizedSearch && !folderMatchesSearch ? matchingAdditionalDocuments : allAdditionalDocuments,
        showAdditionalSlot,
        showNoResult: Boolean(normalizedSearch) && expectedRows.length === 0 && !showAdditionalSlot,
    };
}

export function documentSlotAppearance(documentCount, additional) {
    const hasDocuments = documentCount > 0;
    let background = "#f1f5f3";
    if (additional) background = "#fbfcfb";
    else if (hasDocuments) background = "#f0fdfa";

    let summary = "Attendu";
    if (hasDocuments) summary = `${documentCount} élément${documentCount > 1 ? "s" : ""}`;
    else if (additional) summary = "Ajout libre";

    return {
        background,
        opacity: !additional && !hasDocuments ? 0.78 : 1,
        statusLabel: hasDocuments ? "Champ validé" : "Champ non rempli",
        indicatorBorder: hasDocuments ? "1px solid #17B978" : "1px solid #cbd5d0",
        indicatorBackground: hasDocuments ? "#17B978" : "#eef3f0",
        indicatorColor: hasDocuments ? "#0C3B3C" : "#94a3b8",
        summary,
        summaryColor: hasDocuments ? "#0f766e" : "#5A6D68",
    };
}

// Manière d'afficher un fichier ouvert : lecteur adapté au type, cadre intégré pour PDF et texte, sinon téléchargement seul.
export function previewKind({ fileName, contentType }) {
    const lowerName = fileName?.toLowerCase() || "";
    if (contentType?.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lowerName)) return "image";
    if (contentType?.startsWith("audio/")) return "audio";
    if (contentType?.startsWith("video/")) return "video";
    const isPdf = contentType === "application/pdf" || lowerName.endsWith(".pdf");
    const isText = contentType?.startsWith("text/") || /\.(txt|csv|json|xml|md)$/.test(lowerName);
    return isPdf || isText ? "inline" : "unavailable";
}

export function createPreview(response, fileName) {
    const blob = new Blob([response.data], { type: response.headers?.["content-type"] || "application/octet-stream" });
    return {
        url: URL.createObjectURL(blob),
        fileName: fileName || "Document",
        contentType: blob.type,
    };
}
